import Phaser from "phaser";

/**
 * Visual only sprite to depict a magical portal used when balanced cats exit.
 * The spritesheet is an 8×3 grid (cols×rows) with rows in order: float, open, close.
 */
export class PortalSprite extends Phaser.GameObjects.Sprite {
  // Adjust frame size if the asset differs. Each frame assumed square.
  private static readonly FRAME_WIDTH = 64;
  private static readonly FRAME_HEIGHT = 64;

  /** Preload the portal spritesheet. */
  public static preload(scene: Phaser.Scene) {
    if (scene.textures.exists("portal-sheet")) return;

    scene.load.spritesheet("portal-sheet", "/assets/misc/portal.png", {
      frameWidth: PortalSprite.FRAME_WIDTH,
      frameHeight: PortalSprite.FRAME_HEIGHT,
      endFrame: 24 - 1, // 8 cols × 3 rows = 24 frames (0-23)
    });
  }

  /** Create animations once – safe to call multiple times. */
  public static createAnimations(scene: Phaser.Scene) {
    // Float idle loop (row 0)
    if (!scene.anims.exists("portal-float")) {
      scene.anims.create({
        key: "portal-float",
        frames: scene.anims.generateFrameNumbers("portal-sheet", {
          start: 0,
          end: 7,
        }),
        frameRate: 8,
        repeat: -1,
      });
    }

    // Opening (row 1)
    if (!scene.anims.exists("portal-open")) {
      scene.anims.create({
        key: "portal-open",
        frames: scene.anims.generateFrameNumbers("portal-sheet", {
          start: 8,
          end: 15,
        }),
        frameRate: 12,
        repeat: 0,
      });
    }

    // Closing (row 2)
    if (!scene.anims.exists("portal-close")) {
      scene.anims.create({
        key: "portal-close",
        frames: scene.anims.generateFrameNumbers("portal-sheet", {
          start: 16,
          end: 23,
        }),
        frameRate: 12,
        repeat: 0,
      });
    }
  }

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, "portal-sheet");
    scene.add.existing(this);
    this.setDepth(999); // ensure above cats
    this.setScale(4);

    if (scene.anims.exists("portal-float")) this.play("portal-float");
  }

  /** Play the opening animation then idle float. */
  public open() {
    this.play("portal-open");
    this.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () =>
      this.play("portal-float"),
    );
  }

  /** Play closing animation then destroy the sprite. */
  public close(onClosed?: () => void) {
    this.play("portal-close");
    this.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      onClosed?.();
      this.destroy();
    });
  }
}
